import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  PART_MOVEMENT_TYPE,
  type CreatePartMovementInput,
  type PartMovementType,
} from '@etnrlz/shared';
import { useAddMovement, useMovements, usePart, useVehicles } from '../../lib/queries';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  cx,
} from '../../components/ui';
import { Modal } from '../../components/Modal';
import { QrCode, useQrDataUrl } from '../../components/QrCode';
import { formatDateTime, formatMoney } from '../../lib/format';

const movColor: Record<PartMovementType, string> = {
  ENTRADA: '#22c55e',
  SAIDA: '#ef4444',
  AJUSTE: '#3b82f6',
  RESERVA: '#a855f7',
};

export function PartDetailPage() {
  const { id = '' } = useParams();
  const { data: p, isLoading } = usePart(id);
  const movements = useMovements(id);
  const [movOpen, setMovOpen] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);

  if (isLoading || !p) return <Loading />;
  const low = p.quantidade < p.estoqueMinimo;

  return (
    <div>
      <Link to="/admin/parts" className="text-sm text-muted hover:text-text">← Inventário</Link>
      <PageHeader
        title={p.nome}
        subtitle={`${p.sku}${p.marca ? ` · ${p.marca}` : ''}`}
        actions={
          <>
            <Button variant="secondary" onClick={() => setLabelOpen(true)}>Etiqueta</Button>
            <Button onClick={() => setMovOpen(true)}>Movimentar</Button>
          </>
        }
      />

      <div className="grid lg:grid-cols-3 gap-4">
        <Card>
          <div className="num text-5xl font-bold">{p.quantidade}</div>
          <div className="text-muted text-sm">em estoque (mín. {p.estoqueMinimo})</div>
          {low && <Badge color="#ef4444" className="mt-3">Abaixo do mínimo — repor</Badge>}
          <dl className="mt-4 space-y-2 text-sm border-t border-border pt-4">
            <Info label="Localização" value={p.localizacao ?? '—'} />
            <Info label="Categoria" value={p.categoria ?? '—'} />
            <Info label="Part number" value={p.partNumber ?? '—'} mono />
            <Info label="Custo unit." value={formatMoney(p.custoUnitario)} />
            <Info label="Fornecedor" value={p.fornecedor ?? '—'} />
          </dl>
        </Card>

        <Card className="lg:col-span-2">
          <h2 className="font-semibold mb-3">Movimentações</h2>
          {!movements.data || movements.data.length === 0 ? (
            <EmptyState title="Sem movimentações" />
          ) : (
            <div className="space-y-2">
              {movements.data.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-lg bg-surface-2 px-3 py-2 text-sm">
                  <div className="flex items-center gap-3">
                    <Badge color={movColor[m.tipo]}>{m.tipo}</Badge>
                    <div>
                      <div className="num font-medium">
                        {m.tipo === 'ENTRADA' ? '+' : m.tipo === 'SAIDA' ? '−' : ''}
                        {m.quantidade}
                      </div>
                      <div className="text-xs text-muted">{m.motivo ?? ''}</div>
                    </div>
                  </div>
                  <div className="text-right text-xs text-muted">
                    <div className="num">saldo {m.saldoApos}</div>
                    <div>{formatDateTime(m.timestamp)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <MovementModal partId={id} open={movOpen} onClose={() => setMovOpen(false)} />
      <LabelModal part={p} open={labelOpen} onClose={() => setLabelOpen(false)} />
    </div>
  );
}

function Info({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted">{label}</dt>
      <dd className={mono ? 'num' : ''}>{value}</dd>
    </div>
  );
}

function MovementModal({ partId, open, onClose }: { partId: string; open: boolean; onClose: () => void }) {
  const add = useAddMovement(partId);
  const { data: vehicles } = useVehicles();
  const [f, setF] = useState<CreatePartMovementInput>({ tipo: 'SAIDA', quantidade: 1, userId: 'u-celso' });
  const set = (k: keyof CreatePartMovementInput, v: unknown) => setF((s) => ({ ...s, [k]: v }));
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    try {
      await add.mutateAsync(f);
      onClose();
      setF({ tipo: 'SAIDA', quantidade: 1, userId: 'u-celso' });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erro');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Movimentar estoque"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={add.isPending}>Registrar</Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <Field label="Tipo">
          <Select value={f.tipo} onChange={(e) => set('tipo', e.target.value as PartMovementType)}>
            {PART_MOVEMENT_TYPE.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </Select>
        </Field>
        <Field label={f.tipo === 'AJUSTE' ? 'Novo saldo' : 'Quantidade'}>
          <Input type="number" value={f.quantidade} onChange={(e) => set('quantidade', Number(e.target.value))} />
        </Field>
        <Field label="Veículo (opcional)">
          <Select value={f.vehicleId ?? ''} onChange={(e) => set('vehicleId', e.target.value || undefined)}>
            <option value="">—</option>
            {(vehicles ?? []).map((v) => (
              <option key={v.id} value={v.id}>{v.apelido}</option>
            ))}
          </Select>
        </Field>
        <Field label="Motivo">
          <Input value={f.motivo ?? ''} onChange={(e) => set('motivo', e.target.value)} placeholder="Troca de óleo" />
        </Field>
      </div>
      {err && <p className="text-st-danger text-sm mt-3">{err}</p>}
    </Modal>
  );
}

function LabelModal({
  part,
  open,
  onClose,
}: {
  part: { qrValue: string; nome: string; sku: string; localizacao?: string };
  open: boolean;
  onClose: () => void;
}) {
  const qr = useQrDataUrl(part.qrValue, 220);

  const print = () => {
    const w = window.open('', '_blank', 'width=420,height=320');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>Etiqueta ${part.sku}</title>
      <style>@page{size:62mm 40mm;margin:0}body{margin:0;font-family:monospace;display:flex;gap:10px;align-items:center;padding:8px}
      img{width:120px;height:120px}h1{font-size:14px;margin:0 0 4px}p{margin:2px 0;font-size:11px}</style></head>
      <body><img src="${qr}"/><div><h1>${part.nome}</h1><p>SKU: ${part.sku}</p><p>Local: ${part.localizacao ?? '-'}</p><p>${part.qrValue}</p></div>
      <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),300)}</script></body></html>`);
    w.document.close();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Etiqueta da peça"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Fechar</Button>
          <Button onClick={print}>Imprimir</Button>
        </>
      }
    >
      <div className="flex items-center gap-4 bg-white text-bg rounded-lg p-4">
        <QrCode value={part.qrValue} size={140} />
        <div className={cx('font-mono')}>
          <div className="font-bold">{part.nome}</div>
          <div className="text-sm">SKU: {part.sku}</div>
          <div className="text-sm">Local: {part.localizacao ?? '—'}</div>
          <div className="text-xs mt-1 text-gray-600">{part.qrValue}</div>
        </div>
      </div>
      <p className="text-xs text-muted mt-3">
        Compatível com impressoras térmicas (62×40mm). Use a impressão do navegador.
      </p>
    </Modal>
  );
}
